import { useEffect, useState, type ComponentType } from 'react';
import { isTravoPath, stripLocale } from '@/travo/i18n/locale';
import { loadLegalTexts } from '@/travo/i18n/legal';
import { ROUTES } from '@/travo/routes';
import { isMemberSheepPath } from '@/membersheep/i18n/paths';

/** A lazily loaded JS chunk a page needs before it can render. */
interface Chunk {
  load: () => Promise<unknown>;
  /** The module's source path, as Vite's build manifest keys it; the prerender adds a modulepreload for it. */
  source: string;
}

interface Section extends Chunk {
  /** Renders the loaded section, or nothing while a client-side navigation fetches it. */
  Section: ComponentType;
}

/**
 * A site section in its own JS chunk, so a MemberSheep or Travo Labs page never downloads
 * Travo's Chakra UI and copy (and the other way round). The first render must match the
 * prerendered HTML, so main.tsx loads the page's chunks before hydrating and the prerender
 * awaits all of them (entry-server's `ready`).
 */
const preloadable = (loader: () => Promise<{ default: ComponentType }>, source: string): Section => {
  let loaded: ComponentType | undefined;
  const load = async () => (loaded ??= (await loader()).default);

  const Section = () => {
    const [Loaded, setLoaded] = useState<ComponentType | undefined>(() => loaded);
    useEffect(() => {
      if (Loaded) return;
      // A failed chunk load: a full page load still gets the prerendered page
      load().then(
        (component) => setLoaded(() => component),
        (error: unknown) => {
          console.error(`Could not load ${source}; reloading the page`, error);
          window.location.reload();
        }
      );
    }, [Loaded]);
    return Loaded ? <Loaded /> : null;
  };

  return { load, source, Section };
};

// The source paths must match the import() paths (Vite's manifest keys them by source file)
export const SECTIONS = {
  labs: preloadable(() => import('@/labs/LabsSection'), 'src/labs/LabsSection.tsx'),
  travo: preloadable(() => import('@/travo/TravoSection'), 'src/travo/TravoSection.tsx'),
  memberSheep: preloadable(() => import('@/membersheep/MemberSheepSection'), 'src/membersheep/MemberSheepSection.tsx')
};

/** Travo's privacy policy and terms in every locale (~135 kB gzipped), needed only on those pages. */
export const LEGAL_TEXTS: Chunk = { load: loadLegalTexts, source: 'src/travo/i18n/legal/texts.ts' };

const LEGAL_PATHS: string[] = [ROUTES.privacy, ROUTES.terms];

/** The section that renders a URL path; the Labs section also owns every unknown URL outside the other two. */
const sectionFor = (pathname: string): Section =>
  isTravoPath(pathname) ? SECTIONS.travo : isMemberSheepPath(pathname) ? SECTIONS.memberSheep : SECTIONS.labs;

/** Every lazy chunk a URL path needs for its first render. */
export const pageChunks = (pathname: string): Chunk[] => [
  sectionFor(pathname),
  ...(isTravoPath(pathname) && LEGAL_PATHS.includes(stripLocale(pathname)) ? [LEGAL_TEXTS] : [])
];
